import { Modal, ModalSizeEnum, PrimaryButton, Tooltip, toast } from '@systemeio/ui';
import { BadRequest } from '@systemeio/util';
import { updateRuleEmail, updateRuleSpecificEmail } from 'modules/automation-rules/email/api/rule-email-api';
import { RuleSpecificEmailApiInterface } from 'modules/automation-rules/email/types/rule-email-interface';
import { FilterEmailApiInterface } from 'modules/automation-rules/types/automation-rule-filter-interface';
import React, { useCallback, useEffect, useState } from 'react';
import DashboardMailEditor from 'shared/components/dashboard-mail-editor';
import { useEmail } from 'shared/components/dashboard-mail-editor/hooks/use-email';
import { useEmailWithRecipient } from 'shared/components/dashboard-mail-editor/hooks/use-email-with-recipient';
import { useUnsavedEmail } from 'shared/components/dashboard-mail-editor/hooks/use-unsaved-email';
import { AttachmentFileInterface, EmailBaseInterface, EmailErrorResponseContentInterface, EmailErrorType, EmailId, EmailRecipientErrorResponseContentInterface, EmailRecipientInterface, EmailUpdateInterface, MailingApiInterface, UnsavedAttachmentFileInterface } from 'shared/components/dashboard-mail-editor/types/email-interface';
import { EmailWithRecipientApiInterface } from 'shared/components/dashboard-mail-editor/types/email-with-recipient';
import { getMailingUpdateData } from 'shared/components/dashboard-mail-editor/utils/get-mailing-update-data';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { isValidEmail } from 'shared/utils/email-validation';
import { KeyedMutator } from 'swr';
import { PenIcon } from '@systemeio/ui/icons';
const defaultErrors: EmailErrorType = {
  body: '',
  fromEmail: '',
  fromName: '',
  subject: '',
  previewText: '',
  common: []
};
interface RuleEmailEditModalProps {
  withRecipient?: boolean;
  emailId: EmailId;
  mutateEmailData: KeyedMutator<FilterEmailApiInterface[]> | undefined;
  mutateSpecificEmailData: KeyedMutator<FilterEmailApiInterface[]> | undefined;
  onSetNewEmail?: (data: {
    id: number;
    subject: string;
  }) => void;
}
const RuleEmailEditModal = ({
  withRecipient,
  emailId,
  mutateEmailData,
  mutateSpecificEmailData,
  onSetNewEmail
}: RuleEmailEditModalProps) => {
  const {
    t
  } = useLocoTranslation();
  const [opened, setOpened] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const {
    data: ruleEmail,
    mutate: mutateEmail
  } = useEmail(emailId, !withRecipient && shouldFetch);
  const {
    data: ruleEmailWithRecipient,
    mutate: mutateEmailWithRecipient
  } = useEmailWithRecipient(emailId, !!withRecipient && shouldFetch);
  const [tempState, setTempState] = useState<EmailRecipientInterface>();
  const [attachmentsTempState, setAttachmentsTempState] = useState<(AttachmentFileInterface | UnsavedAttachmentFileInterface)[]>([]);
  const [errors, setErrors] = useState<EmailErrorType>(defaultErrors);
  const setEmail = useCallback((ruleEmail_0: MailingApiInterface | undefined) => {
    if (ruleEmail_0) {
      setTempState(prev => ({
        ...prev,
        body: ruleEmail_0.currentEmail.body,
        subject: ruleEmail_0.currentEmail.subject,
        previewText: ruleEmail_0.currentEmail.previewText,
        fromEmail: ruleEmail_0.currentEmail.fromEmail,
        fromName: ruleEmail_0.currentEmail.fromName,
        recipient: ''
      }));
      setAttachmentsTempState(ruleEmail_0.currentEmail.attachments);
    }
  }, []);
  const setRecipientEmail = useCallback((ruleEmailWithRecipient_0: EmailWithRecipientApiInterface | undefined) => {
    if (ruleEmailWithRecipient_0) {
      setTempState(prev_0 => ({
        ...prev_0,
        recipient: ruleEmailWithRecipient_0.recipient,
        body: ruleEmailWithRecipient_0.currentEmail.body,
        subject: ruleEmailWithRecipient_0.currentEmail.subject,
        previewText: ruleEmailWithRecipient_0.currentEmail.previewText,
        fromEmail: ruleEmailWithRecipient_0.currentEmail.fromEmail,
        fromName: ruleEmailWithRecipient_0.currentEmail.fromName
      }));
      setAttachmentsTempState(ruleEmailWithRecipient_0.currentEmail.attachments);
    }
  }, []);
  useEffect(() => {
    setEmail(ruleEmail);
  }, [ruleEmail, setEmail]);
  useEffect(() => {
    setRecipientEmail(ruleEmailWithRecipient);
  }, [ruleEmailWithRecipient, setRecipientEmail]);
  const onSave = async () => {
    try {
      if (tempState) {
        setErrors(defaultErrors);
        if (!isValidEmail(tempState.fromEmail)) {
          return setErrors(prev_5 => ({
            ...prev_5,
            fromEmail: t('dashboard.validation.email_not_valid')
          }));
        }
        if (withRecipient && !isValidEmail(tempState.recipient)) {
          return setErrors(prev_6 => ({
            ...prev_6,
            recipient: t('dashboard.validation.email_not_valid')
          }));
        }
        setIsFetching(true);
        const currentEmail_0: EmailUpdateInterface = getMailingUpdateData({
          fromEmail: tempState.fromEmail,
          fromName: tempState.fromName,
          subject: tempState.subject,
          previewText: tempState.previewText,
          body: tempState.body
        }, attachmentsTempState);
        const emailResponse = await (withRecipient ? updateRuleSpecificEmail(emailId, {
          recipientAddress: tempState.recipient,
          mailing: {
            currentEmail: currentEmail_0
          }
        }) : updateRuleEmail(emailId, currentEmail_0));
        if (withRecipient) {
          const response = emailResponse.data as RuleSpecificEmailApiInterface;
          await mutateEmailWithRecipient(data => {
            if (data) {
              return {
                ...data,
                recipient: response.recipientAddress,
                currentEmail: {
                  ...response.mailing.currentEmail,
                  fromEmail: response.mailing.currentEmail.fromEmail,
                  fromName: response.mailing.currentEmail.fromName,
                  subject: response.mailing.currentEmail.subject,
                  previewText: response.mailing.currentEmail.previewText,
                  body: response.mailing.currentEmail.body,
                  attachments: response.mailing.currentEmail.attachments
                }
              };
            }
          }, false);
          if (mutateSpecificEmailData) {
            await mutateSpecificEmailData(data_0 => {
              if (data_0) {
                return data_0.map(el => {
                  if (el.id === response.id) {
                    el = {
                      id: el.id,
                      subject: response.mailing.currentEmail.subject
                    };
                  }
                  return el;
                });
              }
            }, false);
          }
        } else {
          const response_0 = emailResponse.data as MailingApiInterface;
          let newData: EmailBaseInterface;
          if (response_0.currentEmail) {
            newData = response_0.currentEmail;
          } else {
            newData = {
              ...currentEmail_0
            };
          }
          if (mutateEmailData) {
            await mutateEmailData(data_1 => {
              if (data_1) {
                return data_1.map(el_0 => {
                  if (el_0.id === ruleEmail?.currentEmail.id) {
                    el_0 = {
                      id: response_0.currentEmail.id,
                      subject: newData.subject
                    };
                  }
                  return el_0;
                });
              }
            }, false);
          }
          await mutateEmail(data_2 => {
            if (data_2) {
              return {
                ...data_2,
                currentEmail: {
                  id: response_0.currentEmail.id,
                  fromEmail: newData.fromEmail,
                  fromName: newData.fromName,
                  subject: newData.subject,
                  previewText: newData.previewText,
                  body: newData.body,
                  attachments: response_0.currentEmail.attachments
                }
              };
            }
          }, false);
          if (onSetNewEmail) {
            onSetNewEmail({
              id: response_0.currentEmail.id,
              subject: response_0.currentEmail.subject
            });
          }
        }
        toast.success(t('global.changes_saved'));
        setIsFetching(false);
        setOpened(false);
        await removeDbRecord();
      }
    } catch (e) {
      setIsFetching(false);
      if (e instanceof BadRequest) {
        if (withRecipient) {
          const errors_0 = e.errors as EmailRecipientErrorResponseContentInterface;
          if (errors_0.fields) {
            if (errors_0.fields.recipientAddress) {
              setErrors(prev_1 => ({
                ...prev_1,
                recipient: errors_0.fields.recipientAddress?.join('')
              }));
            }
            const currentEmail = errors_0.fields.mailing?.currentEmail;
            if (currentEmail) {
              setErrors(prev_2 => ({
                ...prev_2,
                body: currentEmail.body?.join(''),
                fromEmail: currentEmail.fromEmail?.join(''),
                fromName: currentEmail.fromName?.join(''),
                subject: currentEmail.subject?.join(''),
                previewText: currentEmail.previewText?.join('')
              }));
            }
          }
        } else {
          const errors_1 = e.errors as EmailErrorResponseContentInterface;
          if (errors_1.fields) {
            const fields = errors_1.fields as unknown as Record<keyof EmailBaseInterface, string[]>;
            setErrors(prev_3 => ({
              ...prev_3,
              body: fields.body?.join(''),
              fromEmail: fields.fromEmail?.join(''),
              fromName: fields.fromName?.join(''),
              subject: fields.subject?.join(''),
              previewText: fields.previewText?.join('')
            }));
          }
        }
        if (e.errors.common) {
          const commonErrors = e.errors.common;
          setErrors(prev_4 => {
            return {
              ...prev_4,
              common: commonErrors
            };
          });
        }
      } else {
        toast.error(t('global.error'));
      }
    }
  };
  const {
    UnsavedEmailConfirmationModal,
    removeDbRecord,
    setNeedToShowModal
  } = useUnsavedEmail({
    mailingId: withRecipient ? ruleEmailWithRecipient?.id : ruleEmail?.id,
    currentBody: tempState?.body,
    initialBody: withRecipient ? ruleEmailWithRecipient?.currentEmail.body : ruleEmail?.currentEmail.body,
    onPaste: data_3 => setTempState(prev_7 => prev_7 && {
      ...prev_7,
      body: data_3
    })
  });
  const onChangeEmail = (value: EmailRecipientInterface) => setTempState(value);
  const onOpen = () => {
    setOpened(true);
    setShouldFetch(true);
    setNeedToShowModal(true);
  };
  const afterEnter = async () => {
    await mutateEmail();
    await mutateEmailWithRecipient();
  };
  const onAfterLeave = () => {
    setErrors(defaultErrors);
    setShouldFetch(false);
    setEmail(ruleEmail);
    setRecipientEmail(ruleEmailWithRecipient);
  };
  return <>
      <Tooltip mode={'hover'} label={t('dashboard.automation_rule.edit.edit_email')}>
        <button onClick={onOpen} className={'group outline-hidden'}>
          <PenIcon className={'fill-fill-primary main-transition-colors group-hover:fill-brand-primary group-focus-visible:fill-brand-primary h-[16px] w-[16px]'} />
        </button>
      </Tooltip>
      <Modal afterEnter={afterEnter} afterLeave={onAfterLeave} isFetching={isFetching} opened={opened} onClose={() => setOpened(false)} title={t('dashboard.automation_rule.edit.edit_email')} size={ModalSizeEnum.big}>
        <form className="flex flex-col gap-6 lg:gap-10">
          <DashboardMailEditor withRecipient={withRecipient} inModal isPreFetching={withRecipient ? !ruleEmailWithRecipient : !ruleEmail} tempState={tempState} onChangeEmail={onChangeEmail} attachmentsTempState={attachmentsTempState} setAttachmentsTempState={setAttachmentsTempState} errors={errors} />
          <div className={'flex justify-center'}>
            <Tooltip mode="hover" label={t('dashboard.email.warning.update_automation_workflow_email')} disabled={withRecipient ? !ruleEmailWithRecipient : !ruleEmail}>
              <PrimaryButton disabled={withRecipient ? !ruleEmailWithRecipient : !ruleEmail} width="large" type={'submit'} onClick={async (e_0: React.MouseEvent<HTMLButtonElement>) => {
              e_0.preventDefault();
              onSave();
            }} isFetching={isFetching}>
                {t('global.save')}
              </PrimaryButton>
            </Tooltip>
          </div>
        </form>
        {UnsavedEmailConfirmationModal}
      </Modal>
    </>;
};
export default RuleEmailEditModal;